import { resources, counties } from "../data/resources.js";
import {
  resourceCategoryConfig, getResourceCategoryBySlug, getCountyFromSlug,
  getResourceCategoryUrl, getResourceCountyUrl, getResourceCategoryCountyUrl,
  faqSections, toAbsoluteUrl,
} from "./seoPaths.js";

const categoryTitles = {
  "Primary SUD Providers": "Substance Use Disorder (SUD) Treatment & Recovery Support",
  "Primary Mental Health Providers": "Mental Health Providers",
  Housing: "Recovery Housing & Housing Assistance",
  Therapists: "Therapists & Counseling",
  "Ride Services": "Transportation & Ride Services",
  "Crisis Intervention Services": "Crisis Services",
  "Online Resources and Support Groups": "Online Resources & Support Groups",
};

export function filterDirectory(category = "All", county = "All") {
  return resources.filter((resource) =>
    (category === "All" || [resource.category].flat().includes(category)) &&
    (county === "All" || resource.statewide === true || resource.counties.includes(county))
  );
}

export function directoryMetadata(category = "All", county = "All") {
  const subject = category === "All"
    ? "SUD & Mental Health Resource Directory"
    : categoryTitles[category] || category;
  const location = county === "All" ? "New Hampshire" : `${county} County, New Hampshire`;
  const heading = `${subject} in ${location}`;
  const description = category === "All"
    ? `Find substance use disorder (SUD) treatment, mental health providers, recovery housing, peer support, crisis services, and transportation in ${location}.`
    : `Find ${subject.toLowerCase()} in ${location}. Browse provider listings, services, and contact information on Recovery Compass NH.`;
  const path = category !== "All" && county !== "All"
    ? getResourceCategoryCountyUrl(category, county)
    : category !== "All" ? getResourceCategoryUrl(category)
    : county !== "All" ? getResourceCountyUrl(county) : "/resources";
  return { heading, title: `${heading} | Recovery Compass NH`, description, path, url: toAbsoluteUrl(path) };
}

export function getDirectoryRoutes() {
  const routes = [{ category: "All", county: "All" }];
  const categories = Object.keys(resourceCategoryConfig);
  for (const category of categories) routes.push({ category, county: "All" });
  for (const county of counties.filter((name) => name !== "All")) {
    routes.push({ category: "All", county });
    for (const category of categories) {
      if (filterDirectory(category, county).length) routes.push({ category, county });
    }
  }
  return routes.map((route) => ({ ...route, ...directoryMetadata(route.category, route.county) }));
}

export function directoryListSchema(list, url) {
  return {
    "@context": "https://schema.org", "@type": "ItemList",
    name: "Recovery Compass NH resource listings", numberOfItems: list.length,
    itemListElement: list.map((resource, index) => ({
      "@type": "ListItem", position: index + 1,
      item: { "@type": "Thing", name: resource.name, description: resource.description,
        url: `${url}#resource-${encodeURIComponent(resource.id)}` },
    })),
  };
}

export function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[char]));
}

export function serializeSchema(value) {
  return JSON.stringify(value).replace(/</g, "\\u003c");
}

export function getDirectoryPage(categorySlug = null, countySlug = null) {
  const category = categorySlug ? getResourceCategoryBySlug(categorySlug)?.label : "All";
  const county = countySlug ? getCountyFromSlug(countySlug, counties) : "All";
  if (!category || !county) return null;
  const metadata = directoryMetadata(category, county);
  const list = filterDirectory(category, county);
  const esc = escapeHtml;
  const categoryLinks = Object.entries(resourceCategoryConfig).map(([name, config]) =>
    `<li><a href="${county === "All" ? getResourceCategoryUrl(name) : getResourceCategoryCountyUrl(name, county)}">${esc(categoryTitles[name] || config.label)}</a></li>`
  ).join("");
  const countyLinks = counties.filter((name) => name !== "All").map((name) =>
    `<li><a href="${category === "All" ? getResourceCountyUrl(name) : getResourceCategoryCountyUrl(category, name)}">${esc(name)} County</a></li>`
  ).join("");
  const entries = list.map((resource) => `<article id="resource-${esc(resource.id)}">
    <h2>${esc(resource.name)}</h2>
    <p>${esc(resource.description)}</p>
    <p>Services: ${resource.services.map(esc).join(", ")}</p>
    ${resource.address ? `<p>Address: ${esc(resource.address)}</p>` : ""}
    <p>${resource.statewide ? "Available statewide. " : ""}Listed counties: ${resource.counties.filter((name) => name !== "All").map(esc).join(", ") || "See provider for service area"}.</p>
    ${[resource.phone, resource.phone2, resource.phone3, resource.phone4].filter(Boolean).map((phone) => `<p>Phone: <a href="tel:${esc(phone)}">${esc(phone)}</a></p>`).join("")}
    ${/^https?:\/\//i.test(resource.website || "") ? `<p><a href="${esc(resource.website)}">${esc(resource.name)} website</a></p>` : ""}
    ${resource.email ? `<p>Email: <a href="mailto:${esc(resource.email)}">${esc(resource.email)}</a></p>` : ""}
    ${resource.notes ? `<p>${esc(resource.notes)}</p>` : ""}
  </article>`).join("\n");
  const bodyContent = `<nav><a href="/">Home</a> | <a href="/resources">All resources</a> | <a href="/contact">Suggest a resource or correction</a></nav>
    <h1>${esc(metadata.heading)}</h1><p>${esc(metadata.description)}</p>
    <p>${list.length} resource listings${county === "All" ? " across all 10 New Hampshire counties" : ` for ${esc(county)} County`}. The directory includes substance use disorder and mental health services alongside support for housing and transportation.</p>
    <p>Contact providers to confirm current services, eligibility, costs, insurance, and availability. <a href="/contact">Report an outdated listing</a>.</p>
    <nav aria-label="Browse by service"><h2>Browse by service</h2><ul>${categoryLinks}</ul></nav>
    <nav aria-label="Browse by county"><h2>Browse by county</h2><ul>${countyLinks}</ul></nav>
    <section aria-label="Resource listings">${entries || "<p>No listings match this category and county.</p>"}</section>
    <section><h2>Directory questions</h2>${faqSections.resources.map((faq) => `<h3>${esc(faq.question)}</h3><p>${esc(faq.answer)}</p>`).join("")}</section>`;
  return { ...metadata, bodyContent, schema: [
    { "@context": "https://schema.org", "@type": "CollectionPage", name: metadata.heading,
      description: metadata.description, url: metadata.url, inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Recovery Compass NH", url: toAbsoluteUrl("/") } },
    directoryListSchema(list, metadata.url),
    { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqSections.resources.map((faq) => ({
      "@type": "Question", name: faq.question, acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })) },
  ] };
}
